/* =================== EVENT SYSTEM =================== */
/*
 * CENTRALIZED EVENT EMITTER FOR CROSS-STORE COMMUNICATION
 *
 * THIS IMPLEMENTS THE PUBLISH-SUBSCRIBE PATTERN TO DECOUPLE STORES.
 * INSTEAD OF STORES DIRECTLY CALLING EACH OTHER, THEY EMIT AND LISTEN TO EVENTS.
 *
 * BENEFITS:
 * - LOOSE COUPLING: STORES DON'T DEPEND DIRECTLY ON EACH OTHER
 * - SCALABILITY: MULTIPLE LISTENERS CAN SUBSCRIBE TO SAME EVENT
 * - TESTABILITY: STORES CAN BE TESTED IN ISOLATION
 * - MAINTAINABILITY: CLEAR EVENT CONTRACTS AND SEPARATION OF CONCERNS
 *
 * USAGE PATTERN:
 * 1. PUBLISHER: storeEvents.emit(STORE_EVENTS.TRANSACTION_CREATED, transactionData)
 * 2. SUBSCRIBER: storeEvents.on(STORE_EVENTS.TRANSACTION_CREATED, (data) => { ... })
 */

type EventCallback = (data?: unknown) => void;

class EventEmitter {
  private events: Map<string, EventCallback[]> = new Map();

  // SUBSCRIBE TO AN EVENT
  // RETURNS UNSUBSCRIBE FUNCTION FOR CLEANUP
  on(event: string, callback: EventCallback): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(callback);

    return () => this.off(event, callback);
  }

  // UNSUBSCRIBE FROM AN EVENT
  off(event: string, callback: EventCallback): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // PUBLISH AN EVENT TO ALL SUBSCRIBERS
  emit(event: string, data?: unknown): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  // CLEAR ALL EVENT LISTENERS (USEFUL FOR TESTING)
  clear(): void {
    this.events.clear();
  }
}

// GLOBAL EVENT EMITTER INSTANCE
export const storeEvents = new EventEmitter();

/* =================== EVENT CONSTANTS =================== */
/*
 * NAMING CONVENTION: RESOURCE:ACTION
 * - RESOURCE: THE ENTITY BEING AFFECTED (transaction, customer, employee, plan)
 * - ACTION: THE OPERATION PERFORMED (created, updated, deleted, activated, cancelled)
 *
 * EXAMPLES:
 * - transaction:created → WHEN A NEW TRANSACTION IS CREATED
 * - customer:updated → WHEN A CUSTOMER IS UPDATED
 * - plan:activated → WHEN A PLAN STATUS CHANGES TO ACTIVE
 */

export const STORE_EVENTS = {
  TRANSACTION_CREATED: 'transaction:created',
  TRANSACTION_UPDATED: 'transaction:updated',
  TRANSACTION_DELETED: 'transaction:deleted',
  CUSTOMER_CREATED: 'customer:created',
  CUSTOMER_UPDATED: 'customer:updated',
  CUSTOMER_DELETED: 'customer:deleted',
  ACTIVITY_CREATED: 'activity:created',
  ACTIVITY_DELETED: 'activity:deleted',
  EMPLOYEE_CREATED: 'employee:created',
  EMPLOYEE_UPDATED: 'employee:updated',
  EMPLOYEE_DELETED: 'employee:deleted',
  PLAN_CREATED: 'plan:created',
  PLAN_UPDATED: 'plan:updated',
  PLAN_DELETED: 'plan:deleted',
  PLAN_ACTIVATED: 'plan:activated',
  PLAN_CANCELLED: 'plan:cancelled',
} as const;
